import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Globe, Images } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import onboardingIcon from 'assets/images/onboarding-handshake.svg';
import Buddy from 'features/buddy/Buddy';
import { ChatInput } from 'features/chat/components/ChatInput';
import { ChatInputStepHeader } from 'features/chat/components/chat-input-step-header';
import { ButtonIds, initialFeaturesState, type Attachment } from 'features/chat/types/chat';
import { ChoicePromptCard } from 'features/chat/components/choice-prompt-card';
import { hasAddressForms } from 'features/conversation-preferences/address-form';
import type { AddressForm } from 'features/conversation-preferences/services/conversation-preferences-service';
import { BuddySpeechBalloon } from 'features/onboarding/components/buddy-speech-balloon';
import {
  extractOnboardingAnswer,
  type OnboardingNameAnswer,
} from 'features/onboarding/onboarding-answer';
import { startOnboardingConversation } from 'features/onboarding/onboarding-chat';
import {
  buildOnboardingChatUrl,
  isOnboardingComplete,
  useOnboardingDoneElsewhere,
  markOnboardingStepFinished,
  ONBOARDING_INDUSTRY_STEP,
  ONBOARDING_SALUTATION_STEP,
  ONBOARDING_TOTAL_STEPS,
  type OnboardingAnswers,
} from 'features/onboarding/onboarding-journey';
import {
  nameStepPreferences,
  saveOnboardingPreferences,
} from 'features/onboarding/onboarding-preferences';
import { needsSalutationChoice } from 'features/onboarding/onboarding-salutation';
import {
  ONBOARDING_ADDRESS_OPTIONS,
  onboardingAnswerRetry,
  onboardingNameInvalid,
  onboardingNameQuestion,
} from 'features/onboarding/onboarding-script';
import { useChatStore } from 'state/store/chat/use-chat-store';
import { useCommonStore } from 'state/store/common/useCommonStore';
import { useOnboardingStore } from 'state/store/onboarding/use-onboarding-store';
import { useIsMobile } from 'hooks/use-mobile';
import { useToast } from 'hooks/use-toast';
import { Alignment, ButtonTypes, type ChatInputButton } from 'pages/chat/chat';

// Step 1 asks for the user's name here; the salutation (when needed) and step 2 continue in
// the chat page.
const ONBOARDING_STEP = 1;

export default function OnboardingPage() {
  const { t, i18n } = useTranslation();
  const language = i18n?.language;
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const setActiveConversationId = useChatStore((state) => state.setActiveConversationId);
  const userId = useCommonStore((state) => state.user?.id);
  const isFinishedLocally = useOnboardingStore((state) =>
    isOnboardingComplete(userId ? state.progressByUser[userId] : undefined)
  );
  // Also done when another tab or the Klax app finished it (C12).
  const isOnboardingDone = useOnboardingDoneElsewhere() || isFinishedLocally;
  // [Gerne du] / [Lieber Sie]; without a tap the name answer decides, else Sie.
  const [chosenForm, setChosenForm] = useState<AddressForm>();
  const asksAddressForm = hasAddressForms(language);
  // A failed check puts the answer back into the composer.
  const [draft, setDraft] = useState({ text: '', version: 0 });

  const [isFeatureSelected, setIsFeatureSelected] =
    useState<Record<ButtonIds, boolean>>(initialFeaturesState);
  const [selectedSources, setSelectedSources] = useState<string[]>([]);
  const [selectedFolderIdsBySource, setSelectedFolderIdsBySource] = useState<
    Record<string, string[]>
  >({});
  const [isSourcesToolActive, setIsSourcesToolActive] = useState(false);
  const [selectedTools, setSelectedTools] = useState<string[]>([]);
  const [isValidating, setIsValidating] = useState(false);
  const validatingRef = useRef(false);
  const mountedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Same toolbar as the scheduler composer — ChatInput hides its footer when empty.
  const inputButtons = useMemo<ChatInputButton[]>(
    () => [
      {
        id: ButtonIds.SEARCH,
        label: t('SEARCH'),
        icon: <Globe size={16} />,
        onClick: () => {
          setIsFeatureSelected((prev) => ({
            ...initialFeaturesState,
            [ButtonIds.SEARCH]: !prev[ButtonIds.SEARCH],
          }));
        },
        type: isMobile ? ButtonTypes.ICON : ButtonTypes.BUTTON,
        alignment: Alignment.LEFT,
        isDisabled: isFeatureSelected[ButtonIds.CREATE_IMAGE] || selectedSources.length > 0,
        isSelected: isFeatureSelected[ButtonIds.SEARCH],
      },
      {
        id: ButtonIds.CREATE_IMAGE,
        label: t('CREATE_IMAGE'),
        icon: <Images size={16} />,
        onClick: () => {
          setIsFeatureSelected((prev) => ({
            ...initialFeaturesState,
            [ButtonIds.CREATE_IMAGE]: !prev[ButtonIds.CREATE_IMAGE],
          }));
        },
        type: isMobile ? ButtonTypes.ICON : ButtonTypes.BUTTON,
        alignment: Alignment.LEFT,
        isDisabled: selectedSources.length > 0,
        isSelected: isFeatureSelected[ButtonIds.CREATE_IMAGE],
      },
    ],
    [isMobile, isFeatureSelected, t, selectedSources.length]
  );

  // Detach from any previous chat so this landing composer is not bound to it.
  useEffect(() => {
    setActiveConversationId(null);
  }, [setActiveConversationId]);

  const handleSend = async (content: string, _files: Attachment[]) => {
    const response = content.trim();
    if (!response || validatingRef.current || !mountedRef.current) return;

    validatingRef.current = true;
    setIsValidating(true);
    const uiLanguage = language || 'en-US';
    let answer: OnboardingNameAnswer | null;
    try {
      answer = await extractOnboardingAnswer('name', response, uiLanguage);
    } catch {
      if (mountedRef.current) {
        toast({ variant: 'destructive', description: onboardingAnswerRetry(chosenForm) });
        setDraft((previous) => ({ text: response, version: previous.version + 1 }));
      }
      return;
    } finally {
      validatingRef.current = false;
      if (mountedRef.current) setIsValidating(false);
    }
    if (!mountedRef.current) return;
    const name = answer?.givenName.trim();
    if (!answer || !name) {
      toast({ variant: 'warning', description: onboardingNameInvalid(chosenForm) });
      return;
    }

    // English has a single form: no form, no salutation (agents mirror the user).
    let addressForm: AddressForm | undefined;
    if (hasAddressForms(uiLanguage)) {
      addressForm = chosenForm ?? (answer.address === 'informal' ? 'informal' : 'formal');
    }
    const familyName = answer.familyName ?? undefined;
    const inferred =
      addressForm === 'formal' && familyName && answer.salutationGuess !== 'unclear'
        ? answer.salutationGuess
        : undefined;
    const answers: OnboardingAnswers = {
      onboardingName: name,
      addressForm,
      givenName: name,
      familyName,
      salutation: inferred,
      salutationText: undefined,
      salutationInferred: Boolean(inferred),
      industry: undefined,
      industryText: undefined,
    };

    const conversationId = startOnboardingConversation(name, response, answers);
    if (userId) {
      // Clear stale completed/tools from a prior run so step 3 shows chips, not the done card.
      markOnboardingStepFinished(userId, ONBOARDING_STEP, conversationId, {
        ...answers,
        onboardingCompleted: false,
        onboardingTools: '',
        onboardingTeam: '',
      });
    }
    void saveOnboardingPreferences(nameStepPreferences({ ...answers, name }));
    const nextStep = needsSalutationChoice(answers)
      ? ONBOARDING_SALUTATION_STEP
      : ONBOARDING_INDUSTRY_STEP;
    navigate(buildOnboardingChatUrl(conversationId, nextStep), {
      state: {
        preservedFeatures: isFeatureSelected,
        preservedSources: selectedSources,
        preservedFolderIdsBySource: selectedFolderIdsBySource,
        preservedSourcesToolActive: isSourcesToolActive,
        preservedTools: selectedTools,
        ...answers,
        onboardingCompleted: false,
      },
    });
  };

  // A finished journey can't be restarted by typing /onboarding in the address bar.
  if (isOnboardingDone) return <Navigate to="/chat" replace />;

  return (
    <div className="flex h-full min-h-0 flex-col overflow-y-auto overflow-x-hidden">
      <div className="flex flex-1 flex-col items-center justify-center gap-6 px-4 py-6">
        <BuddySpeechBalloon message={onboardingNameQuestion()} />
        <Buddy inline size={192} />
      </div>

      <div className="mx-auto w-full max-w-3xl px-4 pb-6">
        {asksAddressForm ? (
          <ChoicePromptCard
            className="mb-6"
            ariaLabel={onboardingNameQuestion()}
            options={ONBOARDING_ADDRESS_OPTIONS.map((option) => ({
              value: option.value,
              label: t(option.key, { defaultValue: option.defaultValue }),
            }))}
            selectedValue={chosenForm}
            disabled={isValidating}
            onSelect={(option) =>
              setChosenForm(option.value === 'informal' ? 'informal' : 'formal')
            }
          />
        ) : null}
        <ChatInputStepHeader
          icon={<img src={onboardingIcon} alt="" className="size-5 dark:invert" />}
          label={t('ONBOARDING', { defaultValue: 'Onboarding' })}
          step={ONBOARDING_STEP}
          totalSteps={ONBOARDING_TOTAL_STEPS}
        />
        <div className="relative z-10">
          <ChatInput
            key={draft.version}
            initialDraft={draft.text || undefined}
            onSendMessage={handleSend}
            isLoading={isValidating}
            inputButtons={inputButtons}
            isCurrentResponseGenerating={isValidating}
            isFeatureSelected={isFeatureSelected}
            selectedSources={selectedSources}
            onSourcesChange={setSelectedSources}
            selectedFolderIdsBySource={selectedFolderIdsBySource}
            onSelectedFolderIdsBySourceChange={setSelectedFolderIdsBySource}
            isSourcesToolActive={isSourcesToolActive}
            onToggleTool={(toolId) => {
              setIsFeatureSelected((prev) => ({
                ...prev,
                [toolId]: !prev[toolId],
              }));
            }}
            onToggleSourcesTool={() => setIsSourcesToolActive((prev) => !prev)}
            isCancelOnGoing={false}
            selectedTools={selectedTools}
            onSelectedToolsChange={setSelectedTools}
            showQuickActions={false}
            showMoreOptions={false}
          />
        </div>
      </div>
    </div>
  );
}
